'use client';

import {
  ControlType,
  CalculatorId,
  CalculatorType,
  DataTableInputMode,
  type CalculatorConfig,
  type ControlValues,
  type SelectControl,
  type NumberControl
} from '../../../lib/calculators/calculator-config';
import { CriticalValue } from './critical-value';
import { calculateCriticalValue, type DistributionType, type CriticalValueResult } from '../../../lib/stats/critical-value';
import { TableDataJSON } from '../../../src/types/table-json';

export interface CriticalValueControlValues extends ControlValues {
  distributionType: DistributionType;
  alpha: number;
  degreesOfFreedom: number;
  twoTailed: boolean;
}

const distributionTypeControl: SelectControl = {
  id: 'distributionType',
  type: ControlType.Select,
  label: 'Distribution Type',
  required: true,
  options: [
    { value: 'normal', label: 'Normal (Z)' },
    { value: 't', label: 'Student\'s t' },
    { value: 'chi-square', label: 'Chi-square' },
    { value: 'f', label: 'F' }
  ],
  defaultValue: 'normal'
};

const alphaControl: NumberControl = {
  id: 'alpha',
  type: ControlType.Number,
  label: 'Significance Level (α)',
  required: true,
  min: 0.001,
  max: 0.999,
  step: 0.001,
  defaultValue: 0.05
};

const degreesOfFreedomControl: NumberControl = {
  id: 'degreesOfFreedom',
  type: ControlType.Number,
  label: 'Degrees of Freedom (t, χ², F)',
  required: false,
  min: 1,
  max: 1000,
  step: 1,
  defaultValue: 1
};

const twoTailedControl: SelectControl = {
  id: 'twoTailed',
  type: ControlType.Select,
  label: 'Test Type (Z, t)',
  required: true,
  options: [
    { value: 'true', label: 'Two-tailed' },
    { value: 'false', label: 'One-tailed' }
  ],
  defaultValue: 'true'
};

export const criticalValueControls = {
  tabs: [
    {
      title: 'Parameters',
      controls: [
        distributionTypeControl,
        alphaControl,
        degreesOfFreedomControl,
        twoTailedControl
      ]
    }
  ]
};

export const criticalValueConfig: CalculatorConfig<CalculatorId.CRITICAL_VALUE> = {
  id: CalculatorId.CRITICAL_VALUE,
  type: CalculatorType.Table,
  name: 'Critical Value Calculator',
  dataTableInputMode: DataTableInputMode.None,
  controls: criticalValueControls,
  initialControlValues: {
    distributionType: 'normal',
    alpha: 0.05,
    degreesOfFreedom: 1,
    twoTailed: 'true'
  },
  getInitialConfig: () => ({
    distributionType: 'normal',
    alpha: 0.05,
    degreesOfFreedom: 1,
    twoTailed: 'true'
  }),
  getVisualizationData: (params: { data: TableDataJSON; controls: ControlValues }) => {
    const controls = params.controls as CriticalValueControlValues;
    const { distributionType, alpha, degreesOfFreedom, twoTailed } = controls;
    return calculateCriticalValue({
      distributionType,
      alpha,
      degreesOfFreedom: ['t', 'chi-square', 'f'].includes(distributionType) ? degreesOfFreedom : undefined,
      twoTailed: typeof twoTailed === 'string' ? twoTailed === 'true' : twoTailed
    });
  },
  render: ({ visualizationData }) => <CriticalValue visualizationData={visualizationData} />
}; 